import React, { ReactNode } from 'react';
import {
  ExtendableProps,
  InheritableElementProps,
} from '../_internal/components';
import { LooseMarginProps, MarginProps } from '../_internal/spacing';

import styles from './selectableBase.module.scss';

/**
 * The data that `SelectableBase` will pass to the `children` render prop
 */
export type SelectableRenderData = {
  as: 'label';
  /**
   * Whether or not the selectable is disabled
   */
  disabled: boolean;
  /**
   * The hidden input that maintains the selected state
   */
  input: ReactNode;
  /**
   * Whether or not the option is the selected one
   */
  isSelected: boolean;
} & LooseMarginProps;

type CommonProps = {
  /**
   * The render prop for the selectable that renders out its UI. Takes in data about the selectable and returns UI
   */
  children: (data: SelectableRenderData) => ReactNode;
  /**
   * The current selected state of the component
   */
  isSelected: boolean;
  /**
   * A handler that is triggered when the component's selected state changes
   */
  onChange: (isSelected: boolean) => void;
};
type LikeRadioProps = {
  /**
   * The selection behavior of the selectable. `'radio'` acts like a "radio" where clicking when unselected, will select it, but will remain selected when clicked again. `'checkbox'` acts like a "checkbox" where the selected state toggles with every click.
   */
  like: 'radio';
  /**
   * Unique identifier for the selectable (also to be used as the `value` attribute for the hidden input). Optional for `'checkbox'` behavior
   */
  value: string;
};
type LikeCheckboxProps = {
  like: 'checkbox';
  value?: string;
};

type LikeProps = LikeRadioProps | LikeCheckboxProps;

type Props = CommonProps & LikeProps & MarginProps;

/**
 * Allows for the props of a parent component to extend
 * the props of `SelectableBase` *before* they are merged with the
 * props of the underlying element
 */
export type ExtendableSelectableBaseProps<OverrideProps = {}> = Omit<
  InheritableElementProps<'input', ExtendableProps<Props, OverrideProps>>,
  // NOTE: `type` is omitted because we have the narrower `like` prop that only supports
  // checkbox/radio. `checked` has been replaced by `isSelected`
  'type' | 'checked'
>;

export type SelectableBaseProps = ExtendableSelectableBaseProps;

/**
 * `SelectableBase` is a headless / display-free base component. Use it as a building block for creating components that have a selected state. It manages the selected state and core accessibility, while offloading visual display of the UI to the developer via the `children` render prop. Example UIs: checkboxes, radios, selectable pill buttons, etc.
 */
const SelectableBase = ({
  children,
  disabled = false,
  isSelected = false,
  like,
  onChange,
  m,
  mt,
  mb,
  my,
  ml,
  mr,
  mx,
  ...inputProps
}: SelectableBaseProps) => {
  return (
    <>
      {children({
        as: 'label',
        disabled,
        isSelected,
        m,
        mt,
        mb,
        my,
        ml,
        mr,
        mx,
        input: (
          <input
            type={like}
            checked={isSelected}
            disabled={disabled}
            onChange={e => onChange(e.target.checked)}
            className={styles.input}
            {...inputProps}
          />
        ),
      })}
    </>
  );
};

export default SelectableBase;
